import React, { SyntheticEvent, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';

import { baseStyleOfFormElement } from '@r1-frontend/ui-react/experimental/forms/styled';

type TResizableTextareaRowsProps = {
    rows?: never,
    maxRows?: never,
} | {
    rows: number,
    maxRows: number,
}

export type TResizableTextarea = {
    onChange?: React.ChangeEventHandler<HTMLTextAreaElement>,
    placeholder?: string,
    defaultValue?: string,
}

// @todo write documentation with examples (story)
const ResizableTextarea = (
    {
        rows = 1,
        maxRows = 2,

        ...props
    }: TResizableTextarea & TResizableTextareaRowsProps,
): JSX.Element => {
    const ref = useRef(null);
    const initialState = useMemo(() => ({
        initialHeight: 0, // using for flush the textarea height when value is empty (deleted)
        currentHeight: 0,
        amountOfRows: 0, // numbers of displaying rows
        maxRowsReached: rows >= maxRows,
    }), [rows, maxRows]);

    if (rows > maxRows) {
        throw new Error('@r1-frontend/ui-react. The "maxRows" argument value can not be less than the "rows" argument value');
    }

    const [
        state,
        setState,
    ] = useState(initialState);

    useEffect(() => {
        if (ref !== null && ref.current !== null) {
            // @ts-ignore
            setState({ ...state, initialHeight: ref.current.scrollHeight });
        }
    }, [ref]);

    const handleInput = (e: SyntheticEvent) => {
        const { currentHeight, amountOfRows } = state;
        // @ts-ignore
        const { scrollHeight, value } = e.target;

        if (!value) {
            setState({ ...initialState, initialHeight: state.initialHeight });
            return;
        }

        if (currentHeight !== scrollHeight) {
            let _amountOfRows = scrollHeight > currentHeight ? amountOfRows + 1 : amountOfRows - 1;

            // a user deleting input text and count of displaying lines less the maxReachedRows because scrollHeight don't change, so we should display fewer lines forced
            if (_amountOfRows === maxRows && scrollHeight < currentHeight) {
                _amountOfRows -= 1;
            }
            const _state = {
                ...state,
                currentHeight: scrollHeight,
                amountOfRows: _amountOfRows,
                maxRowsReached: _amountOfRows >= maxRows,
            };
            setState(_state);
        }
    };

    return <textarea
        onInput={handleInput}
        onPaste={handleInput}
        onCut={handleInput}
        rows={state.maxRowsReached
            ? maxRows
            : state.amountOfRows > 0
                ? state.amountOfRows
                : rows}
        ref={ref}
        {...props}
    />;
};

export default styled(ResizableTextarea)<TResizableTextarea>`
    ${baseStyleOfFormElement};
    resize: none;
`;
